/**
 * DynamicVisitorForm
 *
 * Renders a visitor registration form driven by a FieldDefinition array
 * fetched from the field-schema endpoint (GET /api/v1/visitors/field-schema/).
 *
 * Each FieldDefinition controls:
 *   - Whether the field is rendered at all (visible)
 *   - Whether it is required
 *   - The input type (text | email | phone | textarea | date | select)
 *   - Label and placeholder
 *
 * The component is fully controlled — it calls `onSubmit` with a
 * flat key/value map of whatever fields are visible and filled.
 *
 * Core fields (first_name, last_name, phone_number) are stored on the
 * Visitor model directly. Non-model keys (company, host_name, etc.) are
 * stored in the `extra_data` JSONField.
 */
import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2, AlertTriangle, UserCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { PhoneNumberInput } from '@/components/shared/PhoneNumberInput'
import type { FieldDefinition, Visitor } from '@/types'

// ── Types ─────────────────────────────────────────────────────────────────────

export type VisitorFormValues = Record<string, string | boolean | undefined>

interface DuplicateVisitorInfo {
  visitor:    Visitor
  onLogVisit: () => void
  onDismiss:  () => void
  isLogging:  boolean
}

interface DynamicVisitorFormProps {
  fields:          FieldDefinition[] | null
  isLoadingConfig: boolean
  /** Phone-blur duplicate detection */
  onPhoneBlur:    (phone: string) => void
  duplicate:      DuplicateVisitorInfo | null
  /**
   * Fast-track pre-fill — when a returning visitor is identified by phone,
   * pass their field values here to pre-populate the form.
   * Keys match FieldDefinition.key; values are strings.
   */
  prefillValues?: Record<string, string>
  /** Final submit */
  onSubmit:       (values: VisitorFormValues) => Promise<void>
  isSubmitting:   boolean
  onCancel:       () => void
  onBack:         () => void
  formId:         string
}

// ── Build Zod schema from field config ────────────────────────────────────────

function buildSchema(fields: FieldDefinition[]) {
  const shape: Record<string, z.ZodTypeAny> = {}
  for (const f of fields) {
    if (!f.visible) continue
    let base: z.ZodTypeAny
    if (f.type === 'email') {
      base = z.string().email('Enter a valid email address')
    } else {
      base = z.string()
    }
    if (f.required) {
      shape[f.key] = (base as z.ZodString).min(1, `${f.label} is required`)
    } else {
      shape[f.key] = base.optional().or(z.literal(''))
    }
  }
  return z.object(shape)
}

// ── DuplicateWarning (inline) ─────────────────────────────────────────────────

function DuplicateWarning({ visitor, onLogVisit, onDismiss, isLogging }: Omit<DuplicateVisitorInfo, never>) {
  return (
    <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" aria-hidden />
        <div className="flex-1 space-y-2">
          <p className="text-sm font-medium text-yellow-800">Existing visitor found</p>
          <p className="text-sm text-yellow-700">
            <span className="font-medium">
              {visitor.first_name} {visitor.last_name}
            </span>{' '}
            is already registered with this phone number (
            {visitor.visit_count} previous {visitor.visit_count === 1 ? 'visit' : 'visits'}).
          </p>
          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              className="h-8 gap-1.5 bg-yellow-700 hover:bg-yellow-800 text-white"
              onClick={onLogVisit}
              disabled={isLogging}
            >
              <UserCheck className="h-3.5 w-3.5" />
              {isLogging ? 'Logging…' : 'Log repeat visit'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-yellow-800 hover:bg-yellow-100"
              onClick={onDismiss}
            >
              Register as new
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function DynamicVisitorForm({
  fields,
  isLoadingConfig,
  onPhoneBlur,
  duplicate,
  prefillValues,
  onSubmit,
  isSubmitting,
  onCancel,
  onBack,
  formId,
}: DynamicVisitorFormProps) {
  const visibleFields = (fields ?? []).filter((f) => f.visible)
  const schema = buildSchema(visibleFields)

  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { errors },
  } = useForm<VisitorFormValues>({
    resolver: zodResolver(schema) as any,
    defaultValues: Object.fromEntries(visibleFields.map((f) => [f.key, ''])),
  })

  // Reset form when fields change (e.g. field schema reloads after config update)
  useEffect(() => {
    if (fields) {
      const defaults = Object.fromEntries(
        fields.filter((f) => f.visible).map((f) => [f.key, ''])
      )
      reset(defaults)
    }
  }, [fields, reset])

  // Fast-track: pre-populate form when a returning visitor is identified by phone
  useEffect(() => {
    if (prefillValues && Object.keys(prefillValues).length > 0) {
      const defaults = Object.fromEntries(visibleFields.map((f) => [f.key, '']))
      reset({ ...defaults, ...prefillValues })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillValues, reset])

  const handleFormSubmit = async (values: VisitorFormValues) => {
    await onSubmit(values)
  }

  if (isLoadingConfig) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading form…
      </div>
    )
  }

  return (
    <>
      <form
        id={formId}
        onSubmit={(e) => void handleSubmit(handleFormSubmit)(e)}
        className="flex-1 space-y-5"
        noValidate
      >
        {visibleFields
          .sort((a, b) => a.order - b.order)
          .map((field) => (
            <FormField
              key={field.key}
              field={field}
              register={register}
              control={control}
              errors={errors}
              onPhoneBlur={field.key === 'phone_number' ? onPhoneBlur : undefined}
              getValues={getValues}
            />
          ))}

        {/* Duplicate visitor warning — appears after phone field */}
        {duplicate && (
          <DuplicateWarning
            visitor={duplicate.visitor}
            onLogVisit={duplicate.onLogVisit}
            onDismiss={duplicate.onDismiss}
            isLogging={duplicate.isLogging}
          />
        )}
      </form>

      {/* Footer actions rendered by parent via SheetFooter — but we expose back */}
      {/* The parent mounts SheetFooter with the form submit button; we expose
          the back button via a portal-like approach by rendering it here */}
      <div className="mt-6 flex gap-2">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onBack}
        >
          ← Back
        </Button>
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form={formId}
          className="flex-1"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Registering…' : 'Register visitor'}
        </Button>
      </div>
    </>
  )
}

// ── Individual field renderer ─────────────────────────────────────────────────

function FormField({
  field,
  register,
  control,
  errors,
  onPhoneBlur,
  getValues,
}: {
  field:       FieldDefinition
  register:    ReturnType<typeof useForm<VisitorFormValues>>['register']
  control:     ReturnType<typeof useForm<VisitorFormValues>>['control']
  errors:      ReturnType<typeof useForm<VisitorFormValues>>['formState']['errors']
  onPhoneBlur?: (phone: string) => void
  getValues:   ReturnType<typeof useForm<VisitorFormValues>>['getValues']
}) {
  const errorMsg = (errors[field.key] as { message?: string } | undefined)?.message
  const inputId = `dyn-${field.key}`

  return (
    <div className="space-y-1.5">
      <Label htmlFor={inputId}>
        {field.label}
        {field.required && ' *'}
      </Label>

      {field.type === 'phone' ? (
        <Controller
          name={field.key}
          control={control}
          render={({ field: f }) => (
            <PhoneNumberInput
              value={String(f.value ?? '')}
              onChange={f.onChange}
              onBlur={() => {
                f.onBlur()
                onPhoneBlur?.(String(getValues(field.key) ?? ''))
              }}
              aria-invalid={!!errorMsg}
            />
          )}
        />
      ) : field.type === 'textarea' ? (
        <Textarea
          id={inputId}
          rows={3}
          placeholder={field.placeholder || undefined}
          className="resize-none"
          aria-invalid={!!errorMsg}
          {...register(field.key)}
        />
      ) : (
        <Input
          id={inputId}
          type={field.type === 'email' ? 'email' : field.type === 'date' ? 'date' : 'text'}
          placeholder={field.placeholder || undefined}
          autoFocus={field.order === 1}
          aria-invalid={!!errorMsg}
          {...register(field.key)}
        />
      )}

      {errorMsg && (
        <p className="text-xs text-destructive">{errorMsg}</p>
      )}
    </div>
  )
}
